import React, { useState } from 'react';
import ExcelJS from 'exceljs';
import Select from 'react-select';
import { formatPrice, LoadingGrid } from '../../../../../rbts/helpers';
import { styleSelect, themeSelect } from '../../../../../rbts/assets/ts/_utils';
import { useObtenerDiasPorEvento } from '../../cortesias/hooks/CortesiasHook';
import {
  useReporteVentasCortesias,
  TicketConCortesia,
  DetalleVenta,
  ResumenTicket,
} from '../hooks/VentasCortesiasHook';

type Props = {};

// ─── Formato de fecha legible ─────────────────────────────────────────────────
const formatFecha = (fechaStr: string) => {
  if (!fechaStr) return '—';
  const d = new Date(fechaStr);
  return d.toLocaleString('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ─── Tabla de detalle de ventas ───────────────────────────────────────────────
const TablaDetalle: React.FC<{ detalle: DetalleVenta[] }> = ({ detalle }) => {
  if (!detalle || detalle.length === 0) {
    return (
      <tr>
        <td colSpan={9} className="text-center text-muted py-3 fst-italic">
          Sin ventas registradas para este ticket
        </td>
      </tr>
    );
  }
  return (
    <>
      {detalle.map((v, idx) => (
        <tr key={`${v.venta_id}-${idx}`} className="align-middle">
          <td className="text-muted fs-7">{v.venta_id}</td>
          <td className="fs-7">{formatFecha(v.fecha_hora_venta)}</td>
          <td className="fw-semibold fs-7">{v.producto ?? '—'}</td>
          <td className="text-center fs-7">{v.cantidad}</td>
          <td className="text-end fs-7">
            {formatPrice(v.precio_unitario ?? 0, 'es-MX', 'MXN', 2, 2)}
          </td>
          <td className="text-end fw-bold fs-7">
            {formatPrice(v.total_venta ?? 0, 'es-MX', 'MXN', 2, 2)}
          </td>
          <td className="text-center fs-7">
            <span
              className={`badge ${
                v.forma_pago === 'Efectivo'
                  ? 'badge-light-warning'
                  : 'badge-light-primary'
              }`}
            >
              {v.forma_pago}
            </span>
          </td>
          <td className="fs-7">{v.punto_venta ?? '—'}</td>
          <td className="fs-7 text-muted">{v.tipo_punto_venta ?? '—'}</td>
        </tr>
      ))}
    </>
  );
};

// ─── Fila de resumen ─────────────────────────────────────────────────────────
const FilaResumen: React.FC<{ resumen: ResumenTicket; folioOrigen?: string | null }> = ({ resumen, folioOrigen }) => {
  const esTransferido = resumen.activo === 0 && !!resumen.folio_nuevo;
  return (
    <tr className="tc-report-subtotal">
      <td className="fs-7 ps-3 tc-report-subtotal-label">
        {formatFecha(resumen.fecha_primera_cortesia)}
      </td>
      <td className="fs-7 fw-bolder tc-report-subtotal-label">
        RESUMEN
      </td>
      <td className="text-center fs-7">
        <span className="badge badge-light-primary">
          {resumen.evento ?? '—'}
        </span>
      </td>
      <td className="text-end fs-7 tc-report-subtotal-label">
        Cortesía:&nbsp;
        <span className="tc-amount-positive">
          {formatPrice(resumen.total_cortesia ?? 0, 'es-MX', 'MXN', 2, 2)}
        </span>
      </td>
      <td className="text-end fs-7 tc-report-subtotal-label">
        Recarga:&nbsp;
        <span className="tc-amount-positive">
          {formatPrice(resumen.saldo_recarga_caja ?? 0, 'es-MX', 'MXN', 2, 2)}
        </span>
      </td>
      <td className="text-center fs-7 tc-report-subtotal-label">
        Consumido:&nbsp;
        <span className="tc-amount-negative">
          {formatPrice(resumen.total_consumido_puesto ?? 0, 'es-MX', 'MXN', 2, 2)}
        </span>
      </td>
      <td className="text-center fs-7">
        {esTransferido && (
          <span className="badge badge-light-warning fw-bold">
            Transferido
          </span>
        )}
        {!esTransferido && (resumen.transferido ?? 0) > 0 && (
          <span className="fs-7 tc-report-subtotal-label">
            {folioOrigen ? `Transferido del Folio ${folioOrigen}:` : 'Transferido:'}&nbsp;
            <span className="tc-amount-positive fw-bold">
              {formatPrice(resumen.transferido, 'es-MX', 'MXN', 2, 2)}
            </span>
          </span>
        )}
      </td>
      <td className="text-end fs-7 tc-report-subtotal-label">
        Balance:&nbsp;
        <span style={{ color: 'var(--tc-primary)' }}>
          {formatPrice(resumen.balance ?? 0, 'es-MX', 'MXN', 2, 2)}
        </span>
      </td>
      <td className="text-end fs-7 tc-report-subtotal-label">
        Saldo Actual:&nbsp;
        <span className="tc-amount-positive">
          {formatPrice(esTransferido ? 0 : (resumen.saldo_actual ?? 0), 'es-MX', 'MXN', 2, 2)}
        </span>
      </td>
    </tr>
  );
};

// ─── Vista principal ──────────────────────────────────────────────────────────
const Vista: React.FC<Props> = () => {
  const [diasSeleccionados, setDiasSeleccionados] = useState<any[]>([]);
  const [folioFiltro, setFolioFiltro] = useState<string>('');
  const [loadingDownload, setLoadingDownload] = useState(false);

  const { list: listaDias, loadingRequest: cargandoDias } = useObtenerDiasPorEvento(0);
  const idsDia = diasSeleccionados.map((d: any) => d.value as number);
  const { list: listRaw, loadingRequest } = useReporteVentasCortesias(idsDia);

  const list = folioFiltro.trim()
    ? listRaw.filter((t) => String(t.folio).toLowerCase().includes(folioFiltro.trim().toLowerCase()))
    : listRaw;

  // ─── Export Excel ───────────────────────────────────────────────────────────
  const handleDownloadExcel = async () => {
    setLoadingDownload(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const ws = workbook.addWorksheet('Ventas con Cortesías');

      // Encabezados combinados
      ws.columns = [
        { header: 'Folio', key: 'folio', width: 12 },
        { header: 'Venta ID', key: 'venta_id', width: 10 },
        { header: 'Fecha/Hora', key: 'fecha_hora_venta', width: 20 },
        { header: 'Producto', key: 'producto', width: 30 },
        { header: 'Cantidad', key: 'cantidad', width: 10 },
        { header: 'Precio Unit.', key: 'precio_unitario', width: 14 },
        { header: 'Total Venta', key: 'total_venta', width: 14 },
        { header: 'Forma Pago', key: 'forma_pago', width: 14 },
        { header: 'Punto de Venta', key: 'punto_venta', width: 20 },
        { header: 'Transferido', key: 'transferido', width: 14 },
      ];

      // Estilo encabezado
      ws.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
      ws.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFF8C24' },
      };

      list.forEach((ticket) => {
        const esTransferido = ticket.resumen?.activo === 0 && !!ticket.resumen?.folio_nuevo;

        // Filas de detalle
        ticket.detalle.forEach((v) => {
          ws.addRow({
            folio: ticket.folio,
            venta_id: v.venta_id,
            fecha_hora_venta: formatFecha(v.fecha_hora_venta),
            producto: v.producto,
            cantidad: v.cantidad,
            precio_unitario: Number(v.precio_unitario),
            total_venta: Number(v.total_venta),
            forma_pago: v.forma_pago,
            punto_venta: v.punto_venta,
            transferido: '',
          });
        });

        const balance = esTransferido ? 0 : Number(ticket.resumen.balance);
        const saldoActual = esTransferido ? 0 : Number(ticket.resumen.saldo_actual);

        // Fila de resumen (destacada)
        const resumenRow = ws.addRow({
          folio: `RESUMEN ${ticket.folio}`,
          venta_id: '',
          fecha_hora_venta: formatFecha(ticket.resumen.fecha_primera_cortesia),
          producto: ticket.resumen.cliente ?? 'Cliente Anónimo',
          cantidad: '',
          precio_unitario: Number(ticket.resumen.total_cortesia),
          total_venta: Number(ticket.resumen.saldo_recarga_caja),
          forma_pago: `Consumido: $${ticket.resumen.total_consumido_puesto}`,
          punto_venta: `Balance: $${balance.toFixed(2)} | Saldo: $${saldoActual.toFixed(2)}`,
          transferido: esTransferido ? 'TRANSFERIDO' : '',
        });
        resumenRow.font = { bold: true, color: { argb: 'FF1A1A1A' } };
        resumenRow.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFFF3E8' },
        };

        // Colorear celda Transferido en naranja si aplica
        if (esTransferido) {
          const transCell = resumenRow.getCell('transferido');
          transCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF8C24' } };
          transCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
          transCell.alignment = { horizontal: 'center' };
        }

        // Fila separadora
        ws.addRow({});
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte_ventas_cortesias.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error al descargar Excel:', err);
    } finally {
      setLoadingDownload(false);
    }
  };

  return (
    <div className="d-flex flex-column flex-xl-row">
      <div className="col-xl-12">
        {/* ── Filtros ───────────────────────────────────────────────── */}
        <div className="card mb-6 mb-xl-9">
          <div className="card-header border-0">
            <h3 className="card-title align-items-start flex-column">
              <span className="card-label fw-bold text-gray-800">Búsqueda</span>
            </h3>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-sm-12 col-md-6 col-lg-5 col-xl-4">
                <div className="fs-7 text-gray-700">
                  <label className="form-label w-auto">Días administrativos:</label>
                  <Select
                    options={listaDias}
                    value={diasSeleccionados}
                    styles={styleSelect}
                    theme={themeSelect}
                    classNamePrefix="tc-select"
                    placeholder="Seleccione uno o varios días..."
                    className="w-100"
                    isSearchable
                    isMulti
                    closeMenuOnSelect={false}
                    isClearable
                    isLoading={cargandoDias}
                    noOptionsMessage={() => <span>No se encontraron registros</span>}
                    onChange={(opts: any) => setDiasSeleccionados(opts ?? [])}
                  />
                </div>
              </div>
              <div className="col-sm-12 col-md-6 col-lg-4 col-xl-3">
                <div className="fs-7 text-gray-700">
                  <label className="form-label w-auto">Folio:</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Buscar por folio..."
                    value={folioFiltro}
                    onChange={(e) => setFolioFiltro(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-6 mb-xl-9">
          {/* ── Header ────────────────────────────────────────────────── */}
          <div className="card-header border-0 pt-5">
            <div className="card-title">
              <h3 className="card-label fw-bolder text-dark">
                Reporte de Ventas con Cortesías
              </h3>
            </div>
            <div className="card-toolbar">
              {list.length > 0 && (
                <button
                  className="btn btn-sm btn-light-success"
                  disabled={loadingDownload}
                  onClick={handleDownloadExcel}
                >
                  {loadingDownload ? (
                    <span className="indicator-progress" style={{ display: 'block' }}>
                      Descargando...
                      <span className="spinner-border spinner-border-sm align-middle ms-2" />
                    </span>
                  ) : (
                    'Descargar Excel'
                  )}
                </button>
              )}
            </div>
          </div>

          {/* ── Body ──────────────────────────────────────────────────── */}
          <div className="card-body pt-3">
            {loadingRequest ? (
              <LoadingGrid text="Cargando reporte..." />
            ) : idsDia.length === 0 ? (
              <div className="text-center text-muted py-10 fs-6">
                Selecciona al menos un día administrativo para ver el reporte.
              </div>
            ) : list.length === 0 ? (
              <div className="text-center text-muted py-10 fs-6">
                No se encontraron tickets con cortesías para los días seleccionados.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-rounded border gy-4 gs-7">
                  <thead>
                    <tr className="fw-bolder fs-7 text-gray-800 border-bottom border-gray-200">
                      <th>ID Venta</th>
                      <th>Fecha / Hora</th>
                      <th>Producto</th>
                      <th className="text-center">Cant.</th>
                      <th className="text-end">Precio Unit.</th>
                      <th className="text-end">Total</th>
                      <th className="text-center">Forma Pago</th>
                      <th>Punto de Venta</th>
                      <th>Tipo</th>
                    </tr>
                  </thead>
                  {list.map((ticket: TicketConCortesia) => {
                    const esTransferido = ticket.resumen?.activo === 0 && !!ticket.resumen?.folio_nuevo;
                    const folioOrigen = list.find(t => t.resumen?.folio_nuevo === ticket.folio)?.folio ?? null;
                    return (
                      <tbody key={ticket.folio} style={{ outline: `1px solid ${esTransferido ? 'var(--tc-primary)' : 'var(--tc-border)'}`, outlineOffset: '-1px' }}>
                        {/* Separador de folio */}
                        <tr className="tc-report-group-header">
                          <td
                            colSpan={9}
                            className="fw-bolder fs-6 ps-3 py-2"
                          >
                            🎫 Folio: {ticket.folio}
                            {ticket.resumen?.evento && (
                              <span className="fw-normal fs-7 ms-3" style={{ color: 'var(--tc-text-muted)' }}>
                                — {ticket.resumen.evento}
                              </span>
                            )}
                          </td>
                        </tr>

                        {/* Filas de detalle */}
                        <TablaDetalle detalle={ticket.detalle} />

                        {/* Fila de resumen */}
                        <FilaResumen resumen={ticket.resumen} folioOrigen={folioOrigen} />

                        {/* Separador visual */}
                        <tr>
                          <td colSpan={9} style={{ height: '12px' }} />
                        </tr>
                      </tbody>
                    );
                  })}
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { Vista };
